<template>
  <a-modal
    :open="true"
    :title="`응시자 관리`"
    :sub="exam ? exam.examName || exam.title : ''"
    size="lg"
    @close="$emit('close')"
  >
    <div v-if="readOnly" class="p-alert p-alert--info">
      <div class="p-alert__title">읽기 전용</div>
      <div class="p-alert__body">완료된 평가는 응시자 명단을 변경할 수 없습니다.</div>
    </div>

    <div v-else-if="addOnly" class="p-alert">
      <div class="p-alert__title">진행 중인 평가</div>
      <div class="p-alert__body">진행 중인 평가는 응시자 추가만 가능합니다. 기존 응시자는 해제할 수 없습니다.</div>
    </div>

    <!-- 검색 / 전체 토글 -->
    <div class="p-exam-search__bar">
      <div class="p-exam-search__input">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <input v-model="query" type="text" placeholder="이름 또는 이메일 검색..." />
      </div>
      <a-btn
        v-if="!readOnly"
        kind="ghost"
        :disabled="filteredStudents.length === 0"
        @click="toggleAll"
      >
        {{ allFilteredSelected ? '전체 해제' : '전체 선택' }}
      </a-btn>
    </div>

    <!-- 상태 카운트 -->
    <div class="p-exam-search__count">
      수강생 <strong>{{ approvedEnrollments.length }}</strong>명 ·
      선택 <strong>{{ selection.size }}</strong>명
      <span v-if="diffCount > 0">· 변경 <strong>{{ diffCount }}</strong>건</span>
    </div>

    <!-- 로딩 -->
    <div v-if="loading" class="p-exam-search__loader">
      <div class="p-spinner" />
      <p>응시자 정보를 불러오는 중...</p>
    </div>

    <!-- 빈 상태 -->
    <div v-else-if="approvedEnrollments.length === 0" class="p-exam-search__empty">
      <div class="p-exam-search__empty-title">승인된 수강생이 없습니다</div>
      <div class="p-exam-search__empty-sub">먼저 수강생을 승인하세요</div>
    </div>

    <!-- 학생 리스트 -->
    <div v-else class="p-exam-search__list">
      <label
        v-for="s in filteredStudents"
        :key="s.users_idx"
        class="p-exam-card"
        :class="{
          'is-selected': selection.has(s.users_idx),
          'is-disabled': isRowDisabled(s),
        }"
      >
        <input
          type="checkbox"
          :checked="selection.has(s.users_idx)"
          :disabled="isRowDisabled(s)"
          @change="toggle(s)"
        />
        <span class="p-exam-card__check" />
        <div class="p-exam-card__body">
          <div class="p-exam-card__head">
            <span class="p-exam-card__title">{{ s.student_name || s.name || '이름 없음' }}</span>
            <span v-if="originalSelection.has(s.users_idx)" class="p-pill p-pill--info">기존</span>
          </div>
          <div class="p-exam-card__meta">
            <span class="p-mono">{{ s.email || '-' }}</span>
          </div>
        </div>
      </label>
    </div>

    <template #foot>
      <a-btn kind="ghost" @click="$emit('close')">취소</a-btn>
      <a-btn
        kind="primary"
        :disabled="readOnly || saving || !canSave"
        @click="save"
      >
        {{ saving ? '저장 중...' : `저장 (${selection.size}명)` }}
      </a-btn>
    </template>
  </a-modal>
</template>

<script>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import AModal from '@/components/Pineapple/AModal.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';
import examService from '@/services/examService.js';

export default {
  name: 'ExamStudentsModal',
  components: { AModal, ABtn },
  props: {
    classroomIdx: { type: [String, Number], required: true },
    exam: { type: Object, required: true },
    enrollments: { type: Array, default: () => [] },
  },
  emits: ['close', 'saved'],
  setup(props, { emit }) {
    const instance = getCurrentInstance();
    const notify = instance?.proxy?.$notifyVue;

    const loading = ref(false);
    const saving = ref(false);
    const query = ref('');
    const selection = ref(new Set());
    const originalSelection = ref(new Set());

    const readOnly = computed(() => props.exam?.status === 'completed');
    const addOnly = computed(() => props.exam?.status === 'in_progress');

    const approvedEnrollments = computed(() =>
      props.enrollments.filter(e => e.status === 'approval' && e.users_idx != null)
    );

    const filteredStudents = computed(() => {
      const q = query.value.trim().toLowerCase();
      if (!q) return approvedEnrollments.value;
      return approvedEnrollments.value.filter(s => {
        const name = (s.student_name || s.name || '').toLowerCase();
        const email = (s.email || '').toLowerCase();
        return name.includes(q) || email.includes(q);
      });
    });

    const allFilteredSelected = computed(() =>
      filteredStudents.value.length > 0 &&
      filteredStudents.value.every(s => selection.value.has(s.users_idx))
    );

    const diffCount = computed(() => {
      let n = 0;
      selection.value.forEach(id => { if (!originalSelection.value.has(id)) n++; });
      originalSelection.value.forEach(id => { if (!selection.value.has(id)) n++; });
      return n;
    });

    const canSave = computed(() => !readOnly.value && diffCount.value > 0);

    const isRowDisabled = (s) => {
      if (readOnly.value) return true;
      if (addOnly.value && originalSelection.value.has(s.users_idx)) return true;
      return false;
    };

    const toggle = (s) => {
      if (isRowDisabled(s)) return;
      const next = new Set(selection.value);
      if (next.has(s.users_idx)) next.delete(s.users_idx);
      else next.add(s.users_idx);
      selection.value = next;
    };

    const toggleAll = () => {
      const next = new Set(selection.value);
      if (allFilteredSelected.value) {
        filteredStudents.value.forEach(s => {
          if (!isRowDisabled(s)) next.delete(s.users_idx);
        });
      } else {
        filteredStudents.value.forEach(s => {
          if (!isRowDisabled(s)) next.add(s.users_idx);
        });
      }
      selection.value = next;
    };

    const loadCurrent = async () => {
      loading.value = true;
      try {
        const list = await examService.fetchExamStudents(props.classroomIdx, props.exam.examId);
        const ids = new Set(
          (Array.isArray(list) ? list : []).map(s => s.users_idx).filter(v => v != null)
        );
        originalSelection.value = ids;
        selection.value = new Set(ids);
      } catch (e) {
        console.error('Error loading exam students:', e);
        if (notify) notify({ type: 'error', title: '응시자 정보를 불러오지 못했습니다.' });
      } finally {
        loading.value = false;
      }
    };

    const save = async () => {
      if (!canSave.value) return;
      saving.value = true;
      try {
        const studentIds = Array.from(selection.value);
        await examService.updateExamStudents(props.classroomIdx, props.exam.examId, studentIds);
        if (notify) notify({ type: 'success', title: '응시자 명단이 저장되었습니다.' });
        emit('saved');
      } catch (e) {
        console.error('Error saving exam students:', e);
        if (notify) notify({ type: 'error', title: e.response?.data?.detail || '응시자 저장에 실패했습니다.' });
      } finally {
        saving.value = false;
      }
    };

    onMounted(loadCurrent);

    return {
      loading,
      saving,
      query,
      selection,
      originalSelection,
      readOnly,
      addOnly,
      approvedEnrollments,
      filteredStudents,
      allFilteredSelected,
      diffCount,
      canSave,
      isRowDisabled,
      toggle,
      toggleAll,
      save,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_pineapple.scss';

.p-exam-search {
  &__bar {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
  }
  &__input {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 12px;
    border: 1px solid $p-line;
    border-radius: 8px;
    background: $p-panel;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus-within {
      border-color: $p-point;
      box-shadow: 0 0 0 3px rgba(255, 201, 60, 0.2);
    }

    svg { color: $p-ink-soft; flex-shrink: 0; }
    input {
      flex: 1;
      border: 0;
      outline: 0;
      background: transparent;
      font-size: 13px;
      color: $p-ink;
      &::placeholder { color: $p-ink-soft; }
    }
  }
  &__count {
    font-size: 12px;
    color: $p-ink-mid;
    padding: 8px 12px;
    background: $p-panel-alt;
    border-radius: 8px;
    margin-bottom: 12px;

    strong { color: $p-ink; font-family: $p-font-mono; }
  }
  &__loader, &__empty {
    padding: 40px 0;
    text-align: center;
    color: $p-ink-mid;
    font-size: 13px;
    p { margin: 12px 0 0; }
  }
  &__empty-title { font-size: 14px; font-weight: 800; color: $p-ink; }
  &__empty-sub { margin-top: 6px; font-size: 12px; color: $p-ink-soft; }
  &__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 420px;
    overflow-y: auto;
  }
}

.p-exam-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid $p-line;
  border-radius: 10px;
  cursor: pointer;
  background: $p-panel;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover:not(.is-disabled) { background: $p-bg; border-color: $p-line-hard; }
  &.is-selected { background: $p-point-soft; border-color: $p-point-deep; }
  &.is-disabled { cursor: not-allowed; opacity: 0.55; background: $p-panel-alt; }

  input { display: none; }
  &__check {
    width: 18px;
    height: 18px;
    border: 2px solid $p-line-hard;
    border-radius: 4px;
    background: $p-panel;
    position: relative;
    flex-shrink: 0;
    margin-top: 2px;
    transition: border-color 0.15s ease, background 0.15s ease;
  }
  input:checked + &__check {
    border-color: $p-point-deep;
    background: $p-ink;
    &::after {
      content: '';
      position: absolute;
      left: 4px; top: 0;
      width: 5px;
      height: 10px;
      border: solid $p-point;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }
  &__body { flex: 1; min-width: 0; }
  &__head {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 4px;
  }
  &__title { font-size: 13px; font-weight: 800; color: $p-ink; }
  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    font-size: 11px;
    color: $p-ink-mid;
  }
}

.p-mono { font-family: $p-font-mono; font-size: 11px; color: $p-ink2; }

.p-pill {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  &--info { background: rgba(60, 132, 255, 0.12); color: #2c6cff; }
}

.p-alert {
  padding: 10px 12px;
  margin-bottom: 12px;
  border-radius: 8px;
  background: $p-panel-alt;
  border: 1px solid $p-line;
  &__title { font-size: 12px; font-weight: 800; color: $p-ink; margin-bottom: 2px; }
  &__body { font-size: 12px; color: $p-ink-mid; }
  &--info { background: rgba(60, 132, 255, 0.08); border-color: rgba(60, 132, 255, 0.2); }
}

.p-spinner {
  display: inline-block;
  width: 36px; height: 36px;
  border: 3px solid $p-line; border-top-color: $p-point;
  border-radius: 50%;
  animation: p-spin 0.8s linear infinite;
}
@keyframes p-spin { to { transform: rotate(360deg); } }
</style>
